import { GlobalContext } from "components/GlobalContext";
import { createFilterableArrayColumnDefinition, IColumnDefinition } from "components/SortableTable";
import ISalesRep, { ISalesRepCreateOrUpdate, ISalesRepFilters, ISalesRepInitResult } from "models/sql/ISalesRep";
import React, { useEffect } from "react";
import { useContext, useRef, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import SalesRepService from "services/SalesRepService";
import { formatDate } from "../../utilities/StringUtility";
import CommonLookupDisplay from "components/CommonLookupDisplay";
import { applyGlobalFilterData } from "utilities/MiscUtilities";
import useLocalStorage from "react-use-localstorage";

interface ISalesRepsLookupProps {
    baseData?: ISalesRepInitResult;
}

const COUNTRIES_FILTER_KEY = "countries";
const STATES_FILTER_KEY = "states";
let filterTimeoutId = 0;

const SalesRepsLookup = (props: ISalesRepsLookupProps) => {
    const globalContext = useContext(GlobalContext);
    const globalContextRef = useRef(globalContext);
    globalContextRef.current = globalContext;
    const [searchParams, setSearchParams] = useSearchParams();
    const [stickyCompany, setStickyCompany] = useLocalStorage("repStickyCompany", searchParams.get("companies") ?? undefined);
    const [tempFilter, setTempFilter] = useState<ISalesRepFilters>({ companies: stickyCompany?.split(",") || undefined });
    const tempFilterRef = useRef(tempFilter);
    tempFilterRef.current = tempFilter;
    const [filter, setFilter] = useState<ISalesRepFilters>(tempFilter);
    const navigate = useNavigate();

    const exportTableDataAsync = async () => {
        const accessToken = await globalContext.getAccessTokenAsync();
        SalesRepService.DownloadExcelFileAsync(
            accessToken,
            "SalesRepsExport.xlsx",
            applyGlobalFilterData(
                STATES_FILTER_KEY,
                COUNTRIES_FILTER_KEY,
                filter,
                globalContextRef.current.region,
                globalContextRef.current.organizationType));
    };

    const getColumns = (): IColumnDefinition<ISalesRep, ISalesRepFilters>[] => {
        return [
            createFilterableArrayColumnDefinition<ISalesRep, ISalesRepFilters>(
                globalContext,
                "fullName",
                "names",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`name${row.salesRepId}`}>
                    {
                        (row.fullName || "").split(",")
                            .map(
                                (e, index, array) => <React.Fragment key={index}>{e}{array.length > 1 && <br></br>}</React.Fragment>)
                    }</td>,
                "Name"),
            createFilterableArrayColumnDefinition<ISalesRep, ISalesRepFilters>(
                globalContext,
                "company",
                "companies",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`company${row.salesRepId}`}>
                    <React.Fragment>{row.dealer?.dealerName || ""}</React.Fragment>
                </td>,
                "Company"),
            createFilterableArrayColumnDefinition<ISalesRep, ISalesRepFilters>(
                globalContext,
                "email",
                "emails",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`email${row.salesRepId}`}>
                    {
                        (row.email || "").split(",")
                            .map(
                                (e, index, array) => <React.Fragment key={index}>{e}{array.length > 1 && <br></br>}</React.Fragment>)
                    }</td>,
                "Email"),
            {
                propName: "salesRepId",
                title: "Manager",
                customRenderer: (value, data) => <td className="align-top" key={`Manager${data.salesRepId}`}>
                    {data.parentSalesRep?.fullName || ""}
                </td>,
                sortable: false
            },
            createFilterableArrayColumnDefinition<ISalesRep, ISalesRepFilters>(
                globalContext,
                "homeState",
                "states",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`homeState${row.salesRepId}`}>
                    {
                        (row.homeState || "").split(",")
                            .map(
                                (e, index, array) => <React.Fragment key={index}>{e}{array.length > 1 && <br></br>}</React.Fragment>)
                    }</td>,
                "State"),
            {
                propName: "salesRepId",
                title: "AC Tags",
                customRenderer: (value, data) => <td className="align-top" key={`Aircards${data.salesRepId}`}>
                    {data.equipment?.filter(e => e.serialNumber?.substring(0, 3).toLowerCase() === "r01").length || 0}
                </td>,
                sortable: true
            },
            {
                propName: "airCardInviteCount",
                title: "AC Regs",
                customRenderer: (value, data) => <td className="align-top" key={`AircardsRegs${data.salesRepId}`}>
                    {data.equipment?.filter(e => e.registrationDate && e.serialNumber?.substring(0, 3).toLowerCase() === "r01").length || 0}
                </td>,
                sortable: true
            },
            {
                propName: "hraInviteCount",
                title: "HRA",
                customRenderer: (value, data) => <td className="align-top" key={`Hras${data.salesRepId}`}>
                    {data.hraInviteCount || 0}/{data.healthRiskAssessments?.length || 0}
                </td>,
                sortable: true
            },
            {
                propName: "lastAccessRequest",
                title: "Last Access Request",
                customRenderer: (value, data) => <td className="align-top" key={`lastAccessRequest${data.salesRepId}`}>
                    {data.lastAccessRequest ? formatDate(data.lastAccessRequest) : ""}
                </td>,
                sortable: true
            },
        ];
    };

    const queFilterUpdate = (newFilter: ISalesRepFilters) => {
        setTempFilter(newFilter);
        clearTimeout(filterTimeoutId);
        filterTimeoutId =
            window.setTimeout(() => {
                setStickyCompany(tempFilterRef.current.companies?.join(",") || "");
            }, 500);
    };

    useEffect(() => {
        if (stickyCompany) {
            setSearchParams({ ...searchParams, companies: stickyCompany || undefined });
            setTempFilter({ companies: stickyCompany?.split(",") || undefined });
            setFilter({ ...filter, companies: stickyCompany?.split(",") || undefined });
        }
        else {
            searchParams.delete("companies");
            setSearchParams({ ...searchParams });
            setTempFilter({ companies: undefined });
            setFilter({ ...filter, companies: undefined });
        }
    }, [stickyCompany]);

    useEffect(() => {
        if (filter.companies?.join(",") !== stickyCompany) {
            setStickyCompany(filter.companies?.join(",") || "");
        }
    }, [filter]);

    const getTools = () => [
        <Form.Control
            type="text"
            style={{
                "display": "inline",
                "width": "150px",
                "position": "relative",
                "left": "-8px"
            }}
            className="float-start full-sm"
            placeholder="Company Filter"
            onChange={
                (e: React.ChangeEvent<HTMLInputElement>) => {
                    queFilterUpdate({
                        ...tempFilter,
                        companies:
                            e.target.value !== "" ?
                                e.target.value?.split(",") :
                                undefined
                    });
                }
            }
            value={tempFilter.companies?.join(",") || ""}
        />,
        <Button
            className="me-2 full-sm"
            key="downloadExcel"
            onClick={() => { exportTableDataAsync(); }}>Export</Button>,
        <>
            {
                globalContext.roles.some(r => r === "Web.Admin") &&
                <Button
                    key="createnew"
                    variant="success"
                    className="full-sm"
                    onClick={() => { navigate(`/Reps/Detail/`) }}>Create New</Button>
            }
        </>
    ];

    return (
        <CommonLookupDisplay<ISalesRepFilters, ISalesRep, ISalesRepCreateOrUpdate>
            baseFilters={{
                sortBys: ["salesRepId"],
                sortDescending: true
            }}
            filterHints={{
                names: "Array",
                states: "Array",
                countries: "Array",
                emails: "Array",
                companies: "Array",
                sortBys: "Array",
                sortDescending: "Boolean",
                dealerIds: "Array",
                repCompanies: "Array"
            }}
            dataTypeName="Representative"
            dataTypeNamePlural="Representatives"
            pathName="Reps"
            primaryIdKey="salesRepId"
            className=""
            countriesFilterKey={COUNTRIES_FILTER_KEY}
            statesFilterKey={STATES_FILTER_KEY}
            filter={filter}
            setFilter={setFilter}
            columnDefinitions={getColumns()}
            service={SalesRepService}
            tools={getTools()}
        />
    );
}

export default SalesRepsLookup;
